import React, { useState, useEffect } from 'react';
import { CalendarCheck, Save, Plus, Trash2, Copy, ExternalLink, Power } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const DAGEN = [
  { k: 'ma', label: 'Maandag' }, { k: 'di', label: 'Dinsdag' }, { k: 'wo', label: 'Woensdag' },
  { k: 'do', label: 'Donderdag' }, { k: 'vr', label: 'Vrijdag' }, { k: 'za', label: 'Zaterdag' }, { k: 'zo', label: 'Zondag' },
];

export default function OnlineAfspraken() {
  const { t } = useT();
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const link = `${window.location.origin}/afspraak/${user?.garageId || ''}`;

  useEffect(() => { carsysApi.getBookingSettings().then(setCfg).catch(() => toast.error(t('Laden mislukt'))); }, []);

  const save = async () => {
    setSaving(true);
    try { const r = await carsysApi.saveBookingSettings(cfg); setCfg(r); toast.success(t('Opgeslagen', 'Opgeslagen')); }
    catch { toast.error(t('Opslaan mislukt')); }
    finally { setSaving(false); }
  };

  const upd = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  const updDienst = (i, k, v) => setCfg(c => ({ ...c, diensten: c.diensten.map((d, idx) => idx === i ? { ...d, [k]: v } : d) }));
  const addDienst = () => setCfg(c => ({ ...c, diensten: [...c.diensten, { id: `d${Date.now().toString(36)}`, naam: '', duurMin: 60 }] }));
  const delDienst = (i) => setCfg(c => ({ ...c, diensten: c.diensten.filter((_, idx) => idx !== i) }));
  const updDag = (dag, k, v) => setCfg(c => ({ ...c, openingstijden: { ...c.openingstijden, [dag]: { ...c.openingstijden[dag], [k]: v } } }));

  const copyLink = () => { navigator.clipboard.writeText(link).then(() => toast.success(t('Link gekopieerd', 'Link gekopieerd'))); };

  if (!cfg) return <div className="cs-empty">{t('Laden...')}</div>;

  return (
    <div data-testid="online-afspraken-page">
      <div className="cs-page-header">
        <div><h2>{t('Online afspraken', 'Online afspraken')}</h2><p>{t('Laat klanten zelf afspraken aanvragen via een deelbare link.', 'Laat klanten zelf afspraken aanvragen via een deelbare link.')}</p></div>
        <button data-testid="btn-save-booking" className="cs-btn primary" onClick={save} disabled={saving}><Save size={14} /> {t('Opslaan')}</button>
      </div>

      {/* Aan/uit + link */}
      <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="booking-toggle-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: cfg.enabled ? 'var(--cs-accent-soft)' : 'var(--cs-card-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: cfg.enabled ? 'var(--cs-accent)' : 'var(--cs-text-muted)' }}>
              <CalendarCheck size={20} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{t('Online afspraken', 'Online afspraken')} {cfg.enabled ? <span className="cs-badge green">{t('Aan', 'Aan')}</span> : <span className="cs-badge gray">{t('Uit', 'Uit')}</span>}</div>
              <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)' }}>{cfg.enabled ? t('Klanten kunnen nu afspraken aanvragen.', 'Klanten kunnen nu afspraken aanvragen.') : t('Zet aan om je boekingspagina te activeren.', 'Zet aan om je boekingspagina te activeren.')}</div>
            </div>
          </div>
          <button data-testid="booking-enable-toggle" className={`cs-btn ${cfg.enabled ? 'danger' : 'primary'}`} onClick={() => upd('enabled', !cfg.enabled)}>
            <Power size={14} /> {cfg.enabled ? t('Uitschakelen', 'Uitschakelen') : t('Inschakelen', 'Inschakelen')}
          </button>
        </div>
        <div style={{ marginTop: 14, padding: 12, background: 'var(--cs-card-alt)', borderRadius: 8 }}>
          <label className="cs-section-title">{t('Jouw boekingslink', 'Jouw boekingslink')} <span style={{ fontWeight: 400, color: 'var(--cs-text-muted)' }}>— {t('deel deze op je website, Google en social media', 'deel deze op je website, Google en social media')}</span></label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="cs-input" readOnly value={link} data-testid="booking-link" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }} />
            <button className="cs-btn" onClick={copyLink} data-testid="booking-copy-link"><Copy size={14} /> {t('Kopiëren', 'Kopiëren')}</button>
            <a className="cs-btn" href={link} target="_blank" rel="noreferrer" data-testid="booking-open-link"><ExternalLink size={14} /> {t('Openen', 'Openen')}</a>
          </div>
        </div>
      </div>

      {/* Diensten met duur */}
      <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="booking-diensten-card">
        <h3 className="cs-card-title"><CalendarCheck size={16} /> {t('Diensten & duur', 'Diensten & duur')}
          <span style={{ fontSize: 11, color: 'var(--cs-text-muted)', fontWeight: 400 }}>— {t('de duur bepaalt automatisch de beschikbare tijdsloten', 'de duur bepaalt automatisch de beschikbare tijdsloten')}</span>
        </h3>
        <table className="cs-table">
          <thead><tr><th>{t('Dienst', 'Dienst')}</th><th style={{ width: 160 }}>{t('Duur (minuten)', 'Duur (minuten)')}</th><th style={{ width: 50 }}></th></tr></thead>
          <tbody>
            {cfg.diensten.map((d, i) => (
              <tr key={d.id} data-testid={`booking-dienst-row-${i}`}>
                <td><input className="cs-input" style={{ padding: 5 }} value={d.naam} onChange={e => updDienst(i, 'naam', e.target.value)} placeholder={t('Naam van de dienst', 'Naam van de dienst')} data-testid={`booking-dienst-naam-${i}`} /></td>
                <td><input type="number" min="15" step="15" className="cs-input" style={{ padding: 5, textAlign: 'right' }} value={d.duurMin} onChange={e => updDienst(i, 'duurMin', parseInt(e.target.value) || 0)} data-testid={`booking-dienst-duur-${i}`} /></td>
                <td><button className="cs-btn ghost danger" onClick={() => delDienst(i)}><Trash2 size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="cs-btn ghost" style={{ marginTop: 8 }} onClick={addDienst} data-testid="booking-add-dienst"><Plus size={13} /> {t('Dienst toevoegen', 'Dienst toevoegen')}</button>
      </div>

      {/* Openingstijden */}
      <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="booking-uren-card">
        <h3 className="cs-card-title"><CalendarCheck size={16} /> {t('Openingstijden', 'Openingstijden')}</h3>
        <table className="cs-table">
          <thead><tr><th style={{ width: 130 }}>{t('Dag', 'Dag')}</th><th style={{ width: 90 }}>{t('Open', 'Open')}</th><th style={{ width: 90 }}>{t('Dicht', 'Dicht')}</th><th>{t('Geopend?', 'Geopend?')}</th></tr></thead>
          <tbody>
            {DAGEN.map(({ k, label }) => {
              const ot = cfg.openingstijden[k] || {};
              return (
                <tr key={k} data-testid={`booking-dag-${k}`} style={{ opacity: ot.actief ? 1 : 0.55 }}>
                  <td style={{ fontWeight: 600 }}>{t(label, label)}</td>
                  <td><input type="time" className="cs-input" style={{ padding: 4 }} value={ot.open || '08:00'} onChange={e => updDag(k, 'open', e.target.value)} disabled={!ot.actief} /></td>
                  <td><input type="time" className="cs-input" style={{ padding: 4 }} value={ot.dicht || '17:00'} onChange={e => updDag(k, 'dicht', e.target.value)} disabled={!ot.actief} /></td>
                  <td>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input type="checkbox" checked={!!ot.actief} onChange={e => updDag(k, 'actief', e.target.checked)} data-testid={`booking-dag-actief-${k}`} />
                      {ot.actief ? t('Geopend', 'Geopend') : t('Gesloten', 'Gesloten')}
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Slot-instellingen */}
      <div className="cs-card padded" style={{ maxWidth: 560 }} data-testid="booking-slot-card">
        <h3 className="cs-card-title"><CalendarCheck size={16} /> {t('Slot-instellingen', 'Slot-instellingen')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="cs-section-title">{t('Interval starttijden (min)', 'Interval starttijden (min)')}</label>
            <input type="number" min="15" step="15" className="cs-input" value={cfg.slotInterval} onChange={e => upd('slotInterval', parseInt(e.target.value) || 30)} data-testid="booking-interval" />
            <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 3 }}>{t('Om de hoeveel minuten een afspraak kan starten (bijv. 30).', 'Om de hoeveel minuten een afspraak kan starten (bijv. 30).')}</div>
          </div>
          <div>
            <label className="cs-section-title">{t('Max. afspraken per tijdslot', 'Max. afspraken per tijdslot')}</label>
            <input type="number" min="1" step="1" className="cs-input" value={cfg.maxPerSlot} onChange={e => upd('maxPerSlot', parseInt(e.target.value) || 1)} data-testid="booking-maxperslot" />
            <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 3 }}>{t('Aantal monteurs/bruggen dat tegelijk kan werken.', 'Aantal monteurs/bruggen dat tegelijk kan werken.')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
